import Badge from '@components/Badge';
import CheckedIcon from '@components/Checkbox/CheckedIcon';
import DefaultIcon from '@components/Checkbox/DefaultIcon';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import { DiscountGiftRow } from '@models/discount';
import { getDDay } from '@utils/couponUtils';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import useGiftRowDisabled from './useGiftRowDisabled';

interface Props {
    gift: DiscountGiftRow;
    isSelected: boolean;
    draftGiftAmount: number;
    alreadyBalanceSelected: boolean;
    onToggle: () => void;
}

/** 선물 한 장 (이름·금액·만료일·체크) */
function GiftRow({
    gift, isSelected, draftGiftAmount, alreadyBalanceSelected, onToggle,
}: Props) {
    const { t } = useTranslation('order');
    const priceUnit = usePriceUnit();
    const {
        title, expiresAt, discountValue, couponType,
    } = gift;
    const { disabled, disableByMinAmount } = useGiftRowDisabled({
        gift, isSelected, draftGiftAmount, alreadyBalanceSelected,
    });
    const dDay = getDDay(expiresAt);
    const amountText = `${discountValue.toLocaleString()}${priceUnit}`;
    const subColor = disabled ? colors.textUnselected : colors.textSecondary;

    return (
        <Container aria-disabled={disabled}
            onClick={() => {
                if (!disabled) {
                    onToggle();
                }
            }}>
            <Contents spacing={2}>
                <Text color={disabled ? colors.textUnselected : colors.textPrimary}>{title}</Text>
                <Text color={subColor}>
                    {couponType === 'BALANCE' ? t('giftSheet.balance', { amount: amountText }) : amountText}
                </Text>
                <Row spacing={2}>
                    <Text size='body-long' color={subColor}>
                        {t('couponPage.until', { date: dayjs(expiresAt).format('YYYY.MM.DD') })}
                    </Text>
                    {dDay !== undefined && (
                        <Badge color={!disabled && dDay <= 7 ? 'red' : 'gray'}>D-{dDay}</Badge>
                    )}
                </Row>
                {disableByMinAmount && (
                    <Text size='body-long' color={colors.textError}>{t('giftSheet.minAmount')}</Text>
                )}
            </Contents>
            {isSelected ? <CheckedIcon/> : <DefaultIcon/>}
        </Container>
    );
}

const Container = styled.div`
    align-items: center;
    background-color: var(--color-background-pure);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    column-gap: var(--spacing-4);
    cursor: pointer;
    display: flex;
    padding: var(--spacing-3) var(--spacing-4);
    width: 100%;

    &[aria-disabled="true"] {
        cursor: default;
    }

    > svg {
        flex-shrink: 0;
    }
`;

const Contents = styled(Column)`
    flex: 1;
    min-width: 0;
`;

export default GiftRow;
